/**
 * Pure scoring for native surveys (VER-108 → VER-109).
 *
 * Scoring rules:
 *   - Only SCORED, VISIBLE, numeric questions contribute to the sum. Questions
 *     hidden by a branching rule are skipped entirely (not counted, not summed).
 *   - Reverse scoring flips a rating value about its own numeric midpoint:
 *       reversed = (min + max) - value
 *     e.g. a 1–4 UCLA item answered "Often"(4) reverse-scores to 1. For WHO-5
 *     (no reverse items) this never fires. The formula is unchanged from VER-108.
 *   - `multi` sums the selected option values (when the question is `scored`).
 *   - `freeText` is never scored.
 *   - Partial results (rawScore/finalScore = null) are returned until every
 *     REQUIRED visible question is answered, so the UI never shows a misleading
 *     partial score.
 */
import type {
    ScaleOption,
    SurveyDefinition,
    SurveyQuestion,
    SurveyResponseValue,
    SurveyResponseValues,
} from "./survey.types";
import { allQuestions, isScorable, questionBounds } from "./survey.types";
import { resolveVisibility } from "./branching";

export const scaleBounds = (options: ScaleOption[]): { min: number; max: number } => {
    const values = options.map((o) => o.value);
    return { min: Math.min(...values), max: Math.max(...values) };
};

/** Whether a response value counts as "answered". 0 and negative numbers ARE answers. */
export const isAnswered = (value: SurveyResponseValue | undefined): boolean => {
    if (value === undefined || value === null || value === "") {
        return false;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === "string") {
        return value.trim().length > 0;
    }
    return true; // number (including 0)
};

/**
 * The numeric contribution of a single answered, scored question.
 * Non-scorable (freeText) or non-scored questions must be filtered out before
 * calling this — it assumes the question can produce a number.
 */
export const questionScore = (
    survey: SurveyDefinition,
    question: SurveyQuestion,
    value: SurveyResponseValue,
): number => {
    const { min, max } = questionBounds(survey, question);

    if (Array.isArray(value)) {
        // multi-choice: sum the selected values (reverse is not applied to sets)
        return value.reduce((sum, v) => sum + v, 0);
    }
    if (typeof value === "number") {
        return question.reverseScored ? min + max - value : value;
    }
    return 0;
};

/**
 * Derive the [min,max] raw-score range from a survey's SCORED questions, for
 * display + persistence. Single-answer numeric items contribute their bounds;
 * a scored multi contributes [0, sum of its option values]. Branching is ignored
 * here — the range is the theoretical span across all scored items.
 */
export const deriveRawRange = (survey: SurveyDefinition): [number, number] => {
    let min = 0;
    let max = 0;
    for (const q of allQuestions(survey)) {
        if (!q.scored || !isScorable(q.type)) {
            continue;
        }
        if (q.type === "multi") {
            const options = q.options ?? [];
            const positiveSum = options.reduce((sum, o) => sum + Math.max(o.value, 0), 0);
            max += positiveSum;
            continue;
        }
        const bounds = questionBounds(survey, q);
        min += bounds.min;
        max += bounds.max;
    }
    return [min, max];
};

export type SurveyScoreResult = {
    /** Number of REQUIRED visible questions answered. */
    answered: number;
    /** Total number of REQUIRED visible questions. */
    total: number;
    /** True only when every required visible question has an answer. */
    complete: boolean;
    /** Sum of reverse-adjusted scored item values (null until complete). */
    rawScore: number | null;
    /** rawScore × multiplier (or rawScore when no multiplier); null until complete. */
    finalScore: number | null;
    /** Cut-off label when finalScore falls below the configured threshold. */
    cutoffLabel: string | null;
};

/**
 * Score a response set against a survey definition. Branching-aware: hidden
 * questions/sections are excluded from both the completion count and the sum.
 */
export const scoreSurvey = (survey: SurveyDefinition, values: SurveyResponseValues): SurveyScoreResult => {
    const { hiddenQuestionIds } = resolveVisibility(survey, values);
    const visible = allQuestions(survey).filter((q) => !hiddenQuestionIds.has(q.id));

    const requiredVisible = visible.filter((q) => q.required);
    const total = requiredVisible.length;
    const answered = requiredVisible.filter((q) => isAnswered(values[q.id])).length;

    const complete = total > 0 && answered === total;
    if (!complete) {
        return { answered, total, complete: false, rawScore: null, finalScore: null, cutoffLabel: null };
    }

    let raw = 0;
    for (const q of visible) {
        if (!q.scored || !isScorable(q.type)) {
            continue;
        }
        const v = values[q.id];
        if (!isAnswered(v)) {
            continue;
        }
        raw += questionScore(survey, q, v);
    }

    const finalScore = survey.scoring.multiplier ? raw * survey.scoring.multiplier : raw;
    const cutoffLabel =
        survey.scoring.cutoff && finalScore < survey.scoring.cutoff.belowValue ? survey.scoring.cutoff.label : null;

    return { answered, total, complete: true, rawScore: raw, finalScore, cutoffLabel };
};
